"use client";

import { GAME_THEATER_PLAYER_BG_CLASS } from "@/lib/constants/layout";
import { Z_INDEX_CLASSES } from "@/lib/constants/z-index";
import { useEffect, useState } from "react";
import { GamePlayer } from "./game-player";
import { GamePlayerFadeIn } from "./game-player-fade-in";
import { GamePlayerLoadingOverlay } from "./game-player-loading";
import { UnityGamePlayer } from "./unity-game-player";

interface DynamicGamePlayerProps {
  game: {
    id: string;
    name: string;
    slug: string;
    url: string | null;
    source_type: string | null;
    mobile_aspect_ratio?: string | null;
    platform_event_tracking_enabled?: boolean;
  };
  fillContainer?: boolean;
}

interface ResolvedGameData {
  url: string | null;
  asset_type?: string;
  asset_id?: string;
  version_id?: string;
  trusted_analytics_origins?: string[];
}

type DynamicGame = DynamicGamePlayerProps["game"];
type CachedResolvedEntry = { data: ResolvedGameData; cachedAt: number };
const RESOLVE_URL_CACHE_TTL_MS = 5 * 60 * 1000;
const resolveUrlCache = new Map<string, CachedResolvedEntry>();
const SHOULD_USE_RESOLVE_URL_CACHE =
  process.env.NODE_ENV !== "test" && typeof process.env.JEST_WORKER_ID === "undefined";

function playerShellClassName(fillContainer: boolean) {
  return fillContainer
    ? `relative w-full h-full overflow-hidden ${GAME_THEATER_PLAYER_BG_CLASS}`
    : "relative w-full aspect-video rounded-3xl overflow-hidden";
}

export function DynamicGamePlayer({ game, fillContainer = false }: DynamicGamePlayerProps) {
  const [resolvedData, setResolvedData] = useState<ResolvedGameData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper function to check if game has direct URL
  const hasDirectUrl = (game: DynamicGame) => {
    return (game.source_type === "external" || !game.source_type) && Boolean(game.url);
  };

  // Helper function to resolve asset-based game URL
  const resolveAssetUrl = async (gameId: string): Promise<ResolvedGameData> => {
    if (SHOULD_USE_RESOLVE_URL_CACHE) {
      const cached = resolveUrlCache.get(gameId);
      if (cached && Date.now() - cached.cachedAt < RESOLVE_URL_CACHE_TTL_MS) {
        return cached.data;
      }
    }

    const response = await fetch(`/api/games/${gameId}/resolve-url`);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to resolve game URL");
    }

    const data = await response.json();
    if (SHOULD_USE_RESOLVE_URL_CACHE) {
      resolveUrlCache.set(gameId, { data, cachedAt: Date.now() });
    }
    return data;
  };

  const applyDirectUrlResolution = (game: DynamicGame) => {
    setLoading(false);
    setError(null);
    setResolvedData({ url: game.url });
  };

  const applyUnavailableResolution = () => {
    setLoading(false);
    setError(null);
    setResolvedData(null);
  };

  const resolveAssetGame = async (gameId: string, isCurrent: () => boolean) => {
    setLoading(true);
    setError(null);
    try {
      const data = await resolveAssetUrl(gameId);
      if (!isCurrent()) return;
      setResolvedData(data);
    } catch (err) {
      if (!isCurrent()) return;
      console.error("Error resolving game URL:", err);
      setResolvedData(null);
      setError(err instanceof Error ? err.message : "Failed to load game");
    } finally {
      if (isCurrent()) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    let isCurrent = true;

    async function resolveUrl() {
      if (hasDirectUrl(game)) {
        applyDirectUrlResolution(game);
        return;
      }

      if (game.source_type === "asset") {
        await resolveAssetGame(game.id, () => isCurrent);
        return;
      }

      applyUnavailableResolution();
    }

    void resolveUrl();

    return () => {
      isCurrent = false;
    };
  }, [game.id, game.url, game.source_type]);

  if (loading) {
    return (
      <div className={playerShellClassName(fillContainer)}>
        <GamePlayerLoadingOverlay className={Z_INDEX_CLASSES.GAME_PLAYER_LOADING_OVERLAY} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${playerShellClassName(fillContainer)} bg-gray-800`}>
        <div className="absolute inset-0 flex items-center justify-center p-8 text-center">
          <div>
            <p className="text-red-400 text-lg mb-2">Failed to load game</p>
            <p className="text-gray-400 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!resolvedData) {
    return (
      <div className={`${playerShellClassName(fillContainer)} bg-gray-800`}>
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="text-gray-400">No game URL available</p>
        </div>
      </div>
    );
  }

  // Check if this is a Unity game
  if (resolvedData.asset_type === "unity" && resolvedData.asset_id && resolvedData.version_id) {
    const unityPlayer = (
      <UnityGamePlayer
        key={`${game.slug}-${resolvedData.asset_id}-${resolvedData.version_id}`}
        gameId={game.id}
        gameName={game.name}
        gameSlug={game.slug}
        assetId={resolvedData.asset_id}
        versionId={resolvedData.version_id}
        assetType={resolvedData.asset_type}
        mobileAspectRatio={game.mobile_aspect_ratio || undefined}
        platformEventTrackingEnabled={Boolean(game.platform_event_tracking_enabled)}
        fillContainer={fillContainer}
      />
    );

    return game.source_type === "asset" ? (
      <GamePlayerFadeIn>{unityPlayer}</GamePlayerFadeIn>
    ) : (
      unityPlayer
    );
  }

  // For non-Unity games, use the regular game player with iframe
  if (!resolvedData.url) {
    return (
      <div className={`${playerShellClassName(fillContainer)} bg-gray-800`}>
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="text-gray-400">No game URL available</p>
        </div>
      </div>
    );
  }

  const iframePlayer = (
    <GamePlayer
      gameId={game.id}
      gameName={game.name}
      gameSlug={game.slug}
      url={resolvedData.url}
      title={game.name}
      assetId={resolvedData.asset_id}
      assetType={resolvedData.asset_type}
      mobileAspectRatio={game.mobile_aspect_ratio || undefined}
      platformEventTrackingEnabled={Boolean(game.platform_event_tracking_enabled)}
      trustedAnalyticsOrigins={resolvedData.trusted_analytics_origins}
      versionId={resolvedData.version_id}
      fillContainer={fillContainer}
    />
  );

  return game.source_type === "asset" ? (
    <GamePlayerFadeIn>{iframePlayer}</GamePlayerFadeIn>
  ) : (
    iframePlayer
  );
}
